<template>
  <div
    v-if="initialized"
    class="flex w-full min-h-screen bg-surface-50 dark:bg-surface-950"
  >
    <div
      class="bg-surface-0 dark:bg-surface-950 w-full md:w-5/12 px-8 py-8 md:px-12 lg:px-20 flex flex-col rounded min-h-[46rem] justify-between"
    >
      <div class="flex items-center gap-4">
        <img
          alt="Logo"
          class="w-2/5 mx-auto"
          :src="picture_logo_url"
        />
      </div>
      <div class="flex-none flex flex-col justify-center gap-12">
        <div class="flex flex-col gap-4">
          <div
            v-if="action"
            class="flex flex-col gap-2"
          >
            <div class="text-surface-900 dark:text-surface-0 text-2xl font-semibold leading-tight">
              {{ $t(`login.title_${action}`) }}
            </div>
          </div>
        </div>
        <Message
          v-if="message_success"
          class="mb-2"
          severity="success"
        >
          {{ message_success }}
        </Message>
        <Message
          v-if="message_error"
          class="mb-2"
          icon="pi pi-times-circle"
          severity="error"
        >
          {{ message_error }}
        </Message>
        <Message
          v-if="message_info"
          severity="secondary"
        >
          {{ message_info }}
        </Message>
        <form @submit.prevent="submit">
          <div class="flex flex-col gap-6">
            <div
              v-if="action && action !== ACTION_EXPIRED_RESET && action !== ACTION_EXPIRED_SIGNUP"
              class="flex flex-col gap-2"
            >
              <label
                class="text-surface-900 dark:text-surface-0 font-medium"
                for="email"
              >
                {{ $t('login.emailAddress') }}
              </label>
              <InputText
                id="email"
                v-model="email"
                autocomplete="username"
                class="w-full p-3 rounded-md shadow-sm"
                :disabled="action === ACTION_RESET || action === ACTION_SIGNUP"
                :invalid="is_email_invalid"
                :placeholder="$t('login.emailAddress')"
                type="text"
                @blur="(email = email.trim()) && (email_touched = true)"
              />
              <Message
                v-if="is_email_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.emailAddressRequired') }}
              </Message>
            </div>
            <div
              v-if="$_.includes([ACTION_LOGIN, ACTION_RESET, ACTION_SIGNUP], action)"
              class="flex flex-col gap-2"
            >
              <label
                class="text-surface-900 dark:text-surface-0 font-medium"
                for="password2"
              >
                {{ $t('login.password') }}
              </label>
              <Password
                v-model="password"
                class="w-full"
                :feedback="false"
                :fluid="true"
                :inputProps="{ autocomplete: action === ACTION_LOGIN ? 'current-password' : 'new-password' }"
                :invalid="is_password_invalid"
                :placeholder="$t('login.password')"
                required
                toggleMask
                @blur="password_touched = true"
              />
              <Message
                v-if="is_password_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordRequired') }}
              </Message>
              <Message
                v-if="is_password_invalid_length"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordLengthRequired') }}
              </Message>
            </div>
            <div
              v-if="$_.includes([ACTION_SIGNUP, ACTION_RESET], action)"
              class="flex flex-col gap-2"
            >
              <label
                class="text-surface-900 dark:text-surface-0 font-medium"
                for="password_confirm"
              >
                {{ $t('login.password_confirm') }}
              </label>
              <Password
                v-model="password_confirm"
                class="w-full"
                :feedback="false"
                :fluid="true"
                :inputProps="{ autocomplete: action === ACTION_LOGIN ? 'current-password' : 'new-password' }"
                :invalid="is_password_confirm_invalid"
                :placeholder="$t('login.password_confirm')"
                required
                toggleMask
                @blur="password_confirm_touched = true"
              />
              <Message
                v-if="is_password_confirm_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordConfirmRequired') }}
              </Message>
              <Message
                v-if="is_password_confirm_match_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.passwordConfirmMatchRequired') }}
              </Message>
            </div>
            <a
              v-if="action === ACTION_LOGIN"
              class="text-primary cursor-pointer text-gray-500 hover:text-gray-600 text-right w-full text-sm -mt-2 -mb-2"
              @click="toggleForgotPassword"
              >{{ $t('login.passwordForgot') }}</a
            >
            <div v-if="action === ACTION_SIGNUP">
              <Checkbox
                v-model="accept_tos"
                binary
                :invalid="is_accept_tos_invalid"
                variant="filled"
                @input="accept_tos_touched = true"
              />
              <label for="accept_tos"
                ><span @click="accept_tos = !accept_tos"> &nbsp;&nbsp;{{ $t('login.acceptTosPrefix') }} </span
                ><u
                  ><a
                    href="https://www.ziggu.io/terms-and-conditions"
                    target="_blank"
                    >{{ $t('login.acceptTos') }}</a
                  ></u
                ></label
              >
              <Message
                v-if="is_accept_tos_invalid"
                severity="error"
                size="small"
                variant="simple"
              >
                {{ $t('login.acceptTosRequired') }}
              </Message>
            </div>
            <div
              v-if="action"
              class="flex flex-wrap items-center gap-6 gap-y-4"
            >
              <Button
                class="w-full !px-12"
                :label="$t(`login.submit_${action}`)"
                type="submit"
              />
              <Button
                v-if="google_sso"
                class="flex-1 min-w-fit whitespace-nowrap mb-2 shrink-0"
                icon="pi pi-google !text-base !leading-none"
                label="Sign in with Google"
                outlined
                severity="secondary"
                type="button"
                @click="loginWithGoogle"
              />
              <a
                v-if="$_.includes([ACTION_FORGOT, ACTION_RESET, ACTION_EXPIRED_RESET], action)"
                class="text-primary cursor-pointer text-gray-500 hover:text-gray-600 text-right w-full text-sm"
                @click="toggleLogin"
              >
                {{ $t('login.backToLogin') }}
              </a>
            </div>
          </div>
        </form>
      </div>
      <div class="mb-1 text-center text-gray-500 text-xs">
        <a
          href="https://ziggu.io"
          target="_blank"
        >
          <div class="flex justify-center items-center mb-2">
            <img
              alt="Ziggu Logo"
              class="h-[15px] mt-1 text-gray-500"
              src="https://storage.googleapis.com/ziggu-engage-public/login/Ziggu-Logo.png"
            />
          </div>
        </a>
        <a
          class="text-gray-500 hover:text-gray-600"
          :href="cookiepolicy_url"
          target="_blank"
          >{{ $t('login.cookiePolicy') }}</a
        >
        -
        <a
          class="text-gray-500 hover:text-gray-600"
          :href="privacypolicy_url"
          target="_blank"
          >{{ $t('login.privacyPolicy') }}</a
        >
        -
        <a
          class="text-gray-500 hover:text-gray-600"
          :href="termsandconditions_url"
          target="_blank"
          >{{ $t('login.termsandconditionsPolicy') }}</a
        >
        -
        <a
          class="text-gray-500 hover:text-gray-600 whitespace-nowrap"
          :href="status_page_url"
          target="_blank"
        >
          <span class="inline-flex items-center">
            Status
            <img
              alt="status page badge"
              class="ml-1 h-4 align-middle"
              src="https://ohdear.app/badges/status-page/01hn3vvv627vwdvpgfchttfe1m/small"
            />
          </span>
        </a>
      </div>
    </div>
    <div
      class="hidden md:block w-7/12"
      :style="{
        height: '100vh',
        backgroundImage: `url('${picture_login_url}')`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundBlendMode: 'overlay, normal',
        boxShadow: '-8px 0 16px -8px rgba(0,0,0,0.08)',
        padding: '0px',
        objectFit: 'cover',
      }"
    />
  </div>
  <div
    v-else
    class="flex items-center justify-center min-h-screen w-full"
  >
    <ProgressSpinner />
  </div>
</template>

<script>
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  inMemoryPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { mapActions } from 'vuex';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { Password } from 'primevue';
import { ZIGGU_PARENT_URL, zigguSubdomainURL } from '@/app/util/urls';
import tinycolor from 'tinycolor2';
import { updatePreset } from '@primeuix/themes';
import * as Sentry from '@sentry/vue';
import ProgressSpinner from 'primevue/progressspinner';

const ACTION_LOGIN = 'login';
const ACTION_FORGOT = 'password_forgot';
const ACTION_RESET = 'password_reset';
const ACTION_EXPIRED_RESET = 'expired_reset';
const ACTION_EXPIRED_SIGNUP = 'expired_signup';
const ACTION_SIGNUP = 'signup';

const MESSAGE_DELAY_MS = 3000;

export default {
  name: 'LoginPage',
  components: {
    Button,
    Checkbox,
    InputText,
    Message,
    Password,
    ProgressSpinner,
  },
  data() {
    return {
      ACTION_LOGIN,
      ACTION_FORGOT,
      ACTION_RESET,
      ACTION_EXPIRED_RESET,
      ACTION_SIGNUP,
      ACTION_EXPIRED_SIGNUP,
      accept_tos: false,
      accept_tos_touched: false,
      action: this.$route.query.action || 'login',
      auth: null,
      cookiepolicy_url: 'https://www.ziggu.io/cookie-policy',
      privacypolicy_url: 'https://www.ziggu.io/privacy-policy',
      termsandconditions_url: 'https://www.ziggu.io/terms-and-conditions',
      email: this.$route.query.email || '',
      email_touched: false,
      initialized: false,
      invitation_code: this.$route.query.invitation_code,
      message_error: null,
      message_info: null,
      message_success: null,
      notification: this.$route.query.notification,
      oob_code: this.$route.query.oob_code,
      password: '',
      password_confirm: '',
      password_confirm_touched: false,
      password_touched: false,
      picture_login_url: null,
      picture_logo_url: null,
      provider_google: null,
      reset_password_expired: false,
      status: null,
      subdomain: this.$route.query.subdomain,
      tenant: null,
      user: null,
    };
  },

  computed: {
    is_email_invalid() {
      return this.email_touched && !this.email;
    },

    is_password_invalid() {
      return this.password_touched && !this.password;
    },

    is_password_invalid_length() {
      if (!_.includes([ACTION_RESET, ACTION_SIGNUP], this.action)) return false;

      return this.password_touched && !this.is_password_invalid && this.password.length < 8;
    },

    is_password_confirm_invalid() {
      return this.password_confirm_touched && !this.password_confirm;
    },

    is_accept_tos_invalid() {
      return this.accept_tos_touched && !this.accept_tos;
    },

    is_password_confirm_match_invalid() {
      if (!_.includes([ACTION_RESET, ACTION_SIGNUP], this.action)) return false;

      return (
        this.password_confirm_touched && !this.is_password_confirm_invalid && this.password !== this.password_confirm
      );
    },

    is_password_reset_invalid() {
      return (
        this.is_password_invalid ||
        this.is_password_invalid_length ||
        this.is_password_confirm_invalid ||
        this.is_password_confirm_match_invalid
      );
    },

    is_signup_invalid() {
      return this.is_password_reset_invalid || this.is_accept_tos_invalid;
    },

    root_url() {
      return !this.subdomain ? ZIGGU_PARENT_URL : zigguSubdomainURL(this.subdomain);
    },

    status_page_url() {
      return `https://status.ziggu.app/?secret=&locale=${this.$i18n.locale}`;
    },

    google_sso() {
      return (
        this.action === ACTION_LOGIN &&
        (this.subdomain === 'supervisor' ||
          (!this.is_email_invalid && this.email.endsWith('@ziggu.io') && this.email.indexOf('+') === -1))
      );
    },
  },

  async created() {
    try {
      // detect whether we're authenticated based on successful response: redirect to root
      await this.FETCH_USER_PROFILE();
      this.redirectToRoot();
      return;
    } catch (error) {
      // ignore 401 unauthorized error: proceed with login render
      if (!(error?.response && error.response.status === 401)) {
        throw error;
      }
    }

    if (this.notification) {
      this.message_info = this.$t(`login.notification_${this.notification}`);
    }

    // firebase init
    this.auth = getAuth();
    await setPersistence(this.auth, inMemoryPersistence); // long-lived session cookies avoid client side persistence
    this.provider_google = new GoogleAuthProvider();

    // fetch tenant configuration to style (and i18n) login page appropriately
    const config = await this.FETCH_TENANT({ subdomain: this.subdomain });
    this.picture_login_url = config.picture_login_url;
    this.picture_logo_url = config.picture_logo_url;
    this.status = config.status;
    this.$i18n.locale = config.language;
    if (config.language !== 'en') {
      this.cookiepolicy_url = this.cookiepolicy_url.replace('www', config.language);
      this.privacypolicy_url = this.privacypolicy_url.replace('www', config.language);
      this.termsandconditions_url = this.termsandconditions_url.replace('www', config.language);
    }
    this.emitter.$emit('initFavicon', config.picture_favicon_url);
    this.initTitle(config.name);
    this.initTheme(config.theme_primary_color);

    if (this.action === ACTION_RESET && this.oob_code) {
      verifyPasswordResetCode(this.auth, this.oob_code).catch((error) => {
        switch (error.code) {
          case 'auth/invalid-action-code':
          case 'auth/expired-action-code':
            this.action = ACTION_EXPIRED_RESET;
            this.message_info = this.$t(`login.auth/invalid-action-code`);
            break;
          default:
            Sentry.captureException(error, { extra: { email: this.email } });
            // with email enumeration protection on it this _should_ only be able to return prior error
            this.message_error = `ERROR ${error.code}: ${error.message}`;
        }
      });
    } else if (this.action === ACTION_SIGNUP) {
      try {
        const invitation_code = `${this.invitation_code}`;
        await this.FETCH_AUTH_USERS({
          email_address: this.email,
          invitation_code,
        });
      } catch (error) {
        const error_code = error.response.data.error_code;
        switch (error_code) {
          case 'auth-ziggu/invalid-invitation-code':
            this.action = ACTION_EXPIRED_SIGNUP;
            this.message_info = this.$t(`login.${error_code}`);
            break;
          default:
            this.message_error = `${error_code}: ${error.message}`;
        }
      }
    }

    this.initialized = true;
  },

  methods: {
    ...mapActions([
      'FETCH_USER_PROFILE',
      'FETCH_TENANT',
      'FETCH_AUTH_USERS',
      'FETCH_USER_AUTH',
      'LINK_USER',
      'LOGIN_USER',
      'PASSWORD_RESET',
    ]),

    loginWithPassword() {
      signInWithEmailAndPassword(this.auth, this.email, this.password)
        .then(async (user_credential) => {
          this.user = user_credential.user;
          await this.createSession();
          this.redirectToRoot();
        })
        .catch((error) => {
          switch (error.code) {
            case 'auth/invalid-credential':
            case 'auth/too-many-requests':
              this.message_error = this.$t(`login.${error.code}`);
              break;
            default:
              this.message_error = `ERROR ${error.code}: ${error.message}`;
              Sentry.captureException(error, { extra: { email: this.email } });
          }
        });
    },

    loginWithGoogle() {
      signInWithPopup(this.auth, this.provider_google)
        .then(async (user_credential) => {
          this.user = user_credential.user;
          await this.createSession();
          this.redirectToRoot();
        })
        .catch((error) => {
          if (error.code === 'auth/popup-blocked') {
            signInWithRedirect(this.auth, this.provider_google).then((user_credential) => {
              this.user = user_credential.user;
              this.createSession();
            });
          } else {
            this.message_error = `ERROR ${error.code}: ${error.message}`;
          }
        });
    },

    submit() {
      if (!this.password) this.password_touched = true;
      if (!this.email) this.email_touched = true;

      switch (this.action) {
        case this.ACTION_LOGIN:
          if (!this.password || !this.email) return;

          this.loginWithPassword();
          break;
        case this.ACTION_SIGNUP:
          if (!this.accept_tos) this.accept_tos_touched = true;
          if (!this.password_confirm) this.password_confirm_touched = true;

          if (this.is_signup_invalid) return;

          createUserWithEmailAndPassword(this.auth, this.email, this.password)
            .then(async (user_credential) => {
              this.user = user_credential.user;
              const id_token = this.user.accessToken;
              const response = await this.LINK_USER({ invitation_code: this.invitation_code, id_token });
              if (response.success) {
                this.action = null;
                this.message_info = this.$t(`login.${response.message}`);
                await this.createSession();
                await new Promise((resolve) => {
                  setTimeout(resolve, MESSAGE_DELAY_MS);
                });
                this.redirectToRoot();
              } else {
                this.message_error = response.message;
              }
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { email: this.email } });
              // Handle errors, e.g. auth/email-already-in-use, invalid-email, operation-not-allowed, weak-password
              this.message_error = `ERROR ${error.code}: ${error.message}`;
            });
          break;
        case this.ACTION_FORGOT:
          this.PASSWORD_RESET({
            subdomain: this.subdomain,
            email_address: this.email,
          })
            .then(() => {
              this.message_info = this.$t(`login.success_${this.action}`, { email: this.email });
            })
            .catch(() => {
              this.message_error = 'An error occurred. Please try again later or contact us.';
            });
          break;
        case this.ACTION_RESET:
          if (!this.password_confirm) this.password_confirm_touched = true;

          if (this.is_password_reset_invalid) return;

          confirmPasswordReset(this.auth, this.oob_code, this.password)
            .then(async () => {
              this.message_info = this.$t(`login.success_${this.action}`, { email: this.email });
              this.action = null;
              await new Promise((resolve) => {
                setTimeout(resolve, MESSAGE_DELAY_MS);
              });
              await this.loginWithPassword();
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { email: this.email } });
              this.message_error = `Error resetting password: ${error.message}`;
              // Handle errors (e.g., weak password, expired code if not pre-verified)
              // Common error codes:
              // - 'auth/expired-action-code': The OOB code has expired.
              // - 'auth/invalid-action-code': The OOB code is invalid. This can happen if the code is malformed or has already been used.
              // - 'auth/user-disabled': The user corresponding to the OOB code has been disabled.
              // - 'auth/user-not-found': There is no user corresponding to the OOB code. This may happen if the user has been deleted.
              // - 'auth/weak-password': The new password is not strong enough.
            });
          break;
        case this.ACTION_EXPIRED_RESET:
          this.toggleForgotPassword();
          break;
        case this.ACTION_EXPIRED_SIGNUP:
          this.toggleLogin();
          break;
        default:
          break;
      }
    },

    async createSession() {
      const id_token = this.user.accessToken;
      await this.LOGIN_USER({ id_token });
    },

    resetValidations() {
      this.password = null;
      this.password_touched = false;
      this.email_touched = false;
    },

    toggleLogin() {
      this.resetValidations();
      this.message_error = null;
      this.message_info = null;
      this.action = ACTION_LOGIN;
    },

    toggleForgotPassword() {
      this.resetValidations();
      this.message_error = null;
      this.message_info = null;
      this.action = ACTION_FORGOT;
    },

    redirectToRoot() {
      window.location.href = this.root_url;
    },

    initTheme(primary_color) {
      function getShade(hex, percent) {
        const color = tinycolor(hex);
        return percent >= 0 ? color.lighten(percent).toHexString() : color.darken(Math.abs(percent)).toHexString();
      }

      updatePreset({
        semantic: {
          primary: {
            50: getShade(primary_color, 45),
            100: getShade(primary_color, 35),
            200: getShade(primary_color, 25),
            300: getShade(primary_color, 15),
            400: getShade(primary_color, 7),
            500: primary_color,
            600: getShade(primary_color, -7),
            700: getShade(primary_color, -15),
            800: getShade(primary_color, -25),
            900: getShade(primary_color, -35),
            950: getShade(primary_color, -45),
          },
        },
      });
    },

    initTitle(tenant_name) {
      document.title = `${tenant_name} Login`;
    },
  },
};
</script>
